// src/pages/auth/VerifyOTP.js

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const VerifyOTP = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyOTP, forgotPassword } = useAuth();
  
  const email = searchParams.get('email');
  const phone = searchParams.get('phone');
  const method = email ? 'email' : 'phone';
  
  const [formData, setFormData] = useState({
    otp: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expiresIn, setExpiresIn] = useState(900);

  useEffect(() => {
    if (!email && !phone) {
      navigate('/auth/forgot-password');
    }
  }, [email, phone, navigate]);

  useEffect(() => {
    if (expiresIn <= 0) return;
    const timer = setInterval(() => {
      setExpiresIn(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [expiresIn]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    
    setFormData(prev => ({
      ...prev,
      [name]: numericValue
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (submitError) setSubmitError('');
    if (successMessage) setSuccessMessage('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.otp) {
      newErrors.otp = 'OTP is required';
    } else if (formData.otp.length !== 6) {
      newErrors.otp = 'OTP must be 6 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (expiresIn <= 0) {
      setSubmitError('This OTP has expired. Please request a new one.');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    setSuccessMessage('');
    
    try {
      
      const requestData = {
        otp: formData.otp.trim(), 
        type: 'password_reset'
      };
      
      if (email) {
        requestData.email = email.toLowerCase().trim();
      } else if (phone) {
        requestData.phone = phone.trim();
      }
      
      
      const result = await verifyOTP(requestData);
      
      
      
      if (result.success) {
       
        const resetToken = result.resetToken || 
                          result.data?.resetToken || 
                          result.data?.data?.resetToken;
        
        if (!resetToken) {
          console.error('❌ No resetToken in response!', result);
          setSubmitError('Server error: Reset token not received. Please try again.');
          return;
        }
        
        if (resetToken.length !== 64) {
          console.error('❌ Invalid token length:', resetToken.length);
          setSubmitError('Server error: Invalid reset token format.');
          return;
        }
        
      
        
        setSuccessMessage('OTP verified successfully! Redirecting...');
        
       
        setTimeout(() => {
          const url = `/auth/reset-password?${method}=${encodeURIComponent(
            email || phone
          )}&token=${encodeURIComponent(resetToken)}`;
          navigate(url);
        }, 1500);
      } else {
        
        const errorMessage = result.message || 'Invalid OTP. Please try again.';
        setSubmitError(errorMessage);
        
      
        if (errorMessage.toLowerCase().includes('invalid')) {
          setFormData({ otp: '' });
        }
      }
    } catch (error) {
      console.error('❌ Verify OTP error:', error);
      
     
      if (error.response) {
    
        const message = error.response.data?.message || 'Invalid OTP. Please try again.';
        setSubmitError(message);
        
      
        console.error('Backend error:', {
          status: error.response.status,
          message: message,
          data: error.response.data
        });
      } else if (error.request) {
     
        setSubmitError('Network error. Please check your connection and try again.');
      } else {
    
        setSubmitError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setIsResending(true);
    setSubmitError('');
    setSuccessMessage('');
    
    try {
      const requestData = method === 'email' 
        ? { email: email.toLowerCase().trim() }
        : { phone: phone.trim() };
        
      const result = await forgotPassword(requestData);
      
      if (result.success) {
        setSuccessMessage('A new OTP has been sent to your ' + method + '.');
        setResendCooldown(120);
        setExpiresIn(900);
        setFormData({ otp: '' });
      } else {
        setSubmitError(result.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Verify OTP</h1>
          <p className="text-gray-600 mt-2">
            Enter the 6-digit code sent to{' '}
            <span className="font-semibold">
              {email || phone}
            </span>
          </p>
        </div>
        
        <form className="space-y-6" onSubmit={handleSubmit}>
          {submitError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-red-600 text-sm">{submitError}</p>
            </div>
          )}
          
          {successMessage && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-green-600 text-sm">{successMessage}</p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-blue-900">
                  Code expires in:
                </span>
              </div>
              <span className={`text-lg font-bold ${
                expiresIn < 300 ? 'text-red-600' : 'text-blue-600'
              }`}>
                {formatTime(expiresIn)}
              </span>
            </div>
            {expiresIn < 300 && expiresIn > 0 && (
              <p className="text-xs text-yellow-700 mt-2">
                ⚠️ Your code will expire soon. Please verify or request a new one.
              </p>
            )}
            {expiresIn === 0 && (
              <p className="text-xs text-red-700 mt-2">
                ❌ This code has expired. Please request a new one below.
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code (OTP)
            </label>
            <input
              type="text"
              name="otp"
              value={formData.otp}
              onChange={handleChange}
              placeholder="Enter 6-digit code"
              maxLength="6"
              className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl font-bold tracking-widest ${
                errors.otp ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              autoFocus
            />
            {errors.otp && (
              <p className="mt-1 text-sm text-red-600">{errors.otp}</p>
            )}
            <p className="text-xs text-gray-600 text-center mt-2">
              Didn't receive the code? Check your spam folder.
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || formData.otp.length !== 6 || expiresIn === 0}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verifying...
              </>
            ) : (
              'Verify OTP'
            )}
          </button>

          <div className="text-center">
            {resendCooldown > 0 ? (
              <p className="text-sm text-gray-600">
                Resend code in{' '}
                <span className="font-semibold text-blue-600">
                  {formatTime(resendCooldown)}
                </span>
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isResending}
                className="text-sm font-medium text-blue-600 hover:text-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? 'Sending...' : 'Resend OTP'}
              </button>
            )}
          </div>

          <div className="text-center space-y-2">
            <Link 
              to="/auth/forgot-password" 
              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Forgot Password
            </Link>
            
            <div className="text-sm text-gray-600">
              Remember your password?{' '}
              <Link 
                to="/auth/login" 
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Sign in here
              </Link>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              Troubleshooting:
            </h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Make sure you entered the code correctly (no spaces)</li>
              <li>• Check your spam/junk folder</li>
              <li>• Wait a few moments for the {method} to arrive</li>
              <li>• Request a new code if the current one expired</li>
              <li>• Ensure you're using the most recent code</li>
            </ul>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyOTP;